import * as React from 'react'
import { Loader2, Plus, Trash2, Type } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { HelpHint } from '@/components/ui/help-hint'
import { useToast } from '@/components/ui/use-toast'
import { apiJson } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { ApplyOverlaysResponse, TextOverlayBlock } from '@/types'

const MAX_BLOCKS = 5

// 3×3 position grid → normalised (x, y). Bottom-centre is the default.
const POSITION_PRESETS: ReadonlyArray<{ key: string; label: string; x: number; y: number }> = [
  { key: 'TL', label: 'Top left',      x: 0.05, y: 0.05 },
  { key: 'TC', label: 'Top centre',    x: 0.50, y: 0.05 },
  { key: 'TR', label: 'Top right',     x: 0.95, y: 0.05 },
  { key: 'ML', label: 'Middle left',   x: 0.05, y: 0.50 },
  { key: 'MC', label: 'Centre',        x: 0.50, y: 0.50 },
  { key: 'MR', label: 'Middle right',  x: 0.95, y: 0.50 },
  { key: 'BL', label: 'Bottom left',   x: 0.05, y: 0.85 },
  { key: 'BC', label: 'Bottom centre', x: 0.50, y: 0.85 },
  { key: 'BR', label: 'Bottom right',  x: 0.95, y: 0.85 },
]

const COLOR_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black' },
  { value: '#FFD700', label: 'Gold' },
  { value: '#FF3B30', label: 'Red' },
  { value: '#34C759', label: 'Green' },
  { value: '#0A84FF', label: 'Blue' },
]

const FONT_SIZE_PRESETS = [28, 40, 52, 72, 96] as const

function defaultBlock(totalDuration: number): TextOverlayBlock {
  // Default to bottom-centre, last 2.5 seconds — the canonical CTA chip.
  const end = Math.max(1.5, totalDuration || 5)
  const start = Math.max(0, end - 2.5)
  return {
    text: '',
    start_s: round(start, 2),
    end_s: round(end, 2),
    x: 0.5,
    y: 0.85,
    font_size: 52,
    color: 'white',
    shadow: true,
    fade_in_s: 0.3,
    fade_out_s: 0.3,
  }
}

function round(n: number, decimals: number): number {
  const f = 10 ** decimals
  return Math.round(n * f) / f
}

function findPresetKey(x: number, y: number): string | null {
  for (const p of POSITION_PRESETS) {
    if (Math.abs(p.x - x) < 0.04 && Math.abs(p.y - y) < 0.04) return p.key
  }
  return null
}

interface Props {
  assemblyId: number
  /** Snapshot from the latest GET /assembly/{id} — auto-suggested by the
   *  stitch task when a storyboard plan is present. */
  initialOverlays: TextOverlayBlock[] | null | undefined
  /** Total stitched duration in seconds (assembly.total_duration_seconds or
   *  sum of scene durations). Drives the timeline-input upper bounds and the
   *  default end_s for fresh blocks. */
  totalDuration: number
  /** Current assembly.status — gates the Apply button. */
  status: string
  /** Called after a successful PATCH or POST so the caller refetches the
   *  assembly. Polling in the parent picks up the status transition. */
  onChanged?: () => void
}

export function TextOverlayEditor({
  assemblyId,
  initialOverlays,
  totalDuration,
  status,
  onChanged,
}: Props) {
  const { toast } = useToast()

  // Local edit buffer. Sync from server snapshot whenever it changes (e.g.
  // after the parent refetches the assembly post-apply or post-stitch).
  const [blocks, setBlocks] = React.useState<TextOverlayBlock[]>(
    () => initialOverlays ?? [],
  )
  const [serverSnapshot, setServerSnapshot] = React.useState<TextOverlayBlock[]>(
    () => initialOverlays ?? [],
  )
  React.useEffect(() => {
    const next = initialOverlays ?? []
    setBlocks(next)
    setServerSnapshot(next)
    // initialOverlays identity is the assembly's `text_overlays`; on every
    // refetch we trust the server as the source of truth and reset local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialOverlays)])

  const [saving, setSaving] = React.useState(false)
  const [applying, setApplying] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Canonical-order serialise so a field edit that triggers React's spread
  // pattern (`{...next[idx], [key]: value}` reorders the changed key to
  // the end) doesn't falsely flag the block as dirty when the value
  // matches the server snapshot. Field list mirrors the TextOverlayBlock
  // interface order in @/types.
  const canonicalise = React.useCallback(
    (b: TextOverlayBlock) => ({
      text: b.text,
      start_s: b.start_s,
      end_s: b.end_s,
      x: b.x,
      y: b.y,
      font_size: b.font_size,
      color: b.color,
      shadow: b.shadow,
      fade_in_s: b.fade_in_s,
      fade_out_s: b.fade_out_s,
    }),
    [],
  )
  const dirty = React.useMemo(
    () =>
      JSON.stringify(blocks.map(canonicalise)) !==
      JSON.stringify(serverSnapshot.map(canonicalise)),
    [blocks, serverSnapshot, canonicalise],
  )
  const isApplying = status === 'applying_overlays'
  // Apply is only valid from the terminal 'stitched' state — when the
  // server is mid-render or stitching, disable both Save + Apply so the
  // user can't double-trigger.
  const canEdit = !saving && !applying && !isApplying

  const handleAdd = () => {
    if (blocks.length >= MAX_BLOCKS) return
    setBlocks((prev) => [...prev, defaultBlock(totalDuration)])
  }

  const handleRemove = (idx: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleUpdate = <K extends keyof TextOverlayBlock>(
    idx: number,
    key: K,
    value: TextOverlayBlock[K],
  ) => {
    setBlocks((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [key]: value }
      return next
    })
  }

  const handleSetPosition = (idx: number, x: number, y: number) => {
    setBlocks((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], x, y }
      return next
    })
  }

  const validate = (b: TextOverlayBlock, i: number): string | null => {
    if (!b.text.trim()) return `Block ${i + 1}: text is empty`
    if (b.end_s <= b.start_s) return `Block ${i + 1}: end must be after start`
    if (b.start_s < 0) return `Block ${i + 1}: start cannot be negative`
    if (totalDuration > 0 && b.end_s > totalDuration + 0.5) {
      return `Block ${i + 1}: end (${b.end_s}s) exceeds video duration (${totalDuration}s)`
    }
    return null
  }

  const handleSave = async (): Promise<boolean> => {
    setError(null)
    for (let i = 0; i < blocks.length; i++) {
      const err = validate(blocks[i], i)
      if (err) {
        setError(err)
        return false
      }
    }
    setSaving(true)
    try {
      await apiJson(`/api/v1/assembly/${assemblyId}/text-overlays`, {
        method: 'PATCH',
        body: JSON.stringify({ text_overlays: blocks }),
      })
      setServerSnapshot(blocks)
      onChanged?.()
      toast({ title: 'Text overlays saved', description: 'Click "Apply" to burn them onto the video.' })
      return true
    } catch (err) {
      setError((err as Error).message)
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleApply = async () => {
    setError(null)
    // If the user has unsaved edits, save first so the worker sees the
    // current buffer. Failing the save short-circuits the apply.
    if (dirty) {
      const ok = await handleSave()
      if (!ok) return
    }
    setApplying(true)
    try {
      await apiJson<ApplyOverlaysResponse>(
        `/api/v1/assembly/${assemblyId}/apply-overlays`,
        { method: 'POST' },
      )
      toast({
        title: 'Rendering text onto video…',
        description: 'Typically 20–60s. The player refreshes when it lands.',
      })
      onChanged?.()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setApplying(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-1.5">
          <Type className="h-4 w-4" />
          Text overlays
          <Badge variant="outline" className="text-[10px] font-normal">
            {blocks.length}/{MAX_BLOCKS}
          </Badge>
          <HelpHint text="Burn-in text added AFTER generation. Seedance can't render readable text in-frame, so brand names, prices, hooks and CTAs go here instead. Each block is a separate piece of text with its own timing, position, font size and color. Up to 5 blocks per video." />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isApplying && (
          <div className="flex items-center gap-2 rounded-md border border-foreground/30 bg-foreground/5 px-3 py-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Rendering text onto video. The player will refresh when it's ready.
          </div>
        )}

        {blocks.length === 0 ? (
          <div className="text-center py-6 space-y-2">
            <Type className="h-8 w-8 mx-auto text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground">
              No text overlays yet. Add one to burn a CTA, price tag, or hook onto the video.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {blocks.map((b, idx) => {
              const presetKey = findPresetKey(b.x, b.y)
              return (
                <div key={idx} className="rounded-md border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Block {idx + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      onClick={() => handleRemove(idx)}
                      disabled={!canEdit}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>

                  {/* Text */}
                  <div className="space-y-1.5">
                    <Label htmlFor={`overlay-${idx}-text`}>Text</Label>
                    <Textarea
                      id={`overlay-${idx}-text`}
                      value={b.text}
                      onChange={(e) => handleUpdate(idx, 'text', e.target.value.slice(0, 120))}
                      placeholder="e.g. 50% OFF — TODAY ONLY"
                      rows={2}
                      maxLength={120}
                      className="text-sm resize-none"
                      disabled={!canEdit}
                    />
                    <div className="flex justify-end text-[10px] text-muted-foreground tabular-nums">
                      {b.text.length}/120
                    </div>
                  </div>

                  {/* Timing */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor={`overlay-${idx}-start`} className="text-xs">
                        Start (s)
                      </Label>
                      <Input
                        id={`overlay-${idx}-start`}
                        type="number"
                        step="0.1"
                        min={0}
                        max={Math.max(totalDuration, 0.1)}
                        value={b.start_s}
                        onChange={(e) =>
                          handleUpdate(idx, 'start_s', Math.max(0, Number(e.target.value) || 0))
                        }
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`overlay-${idx}-end`} className="text-xs">
                        End (s)
                      </Label>
                      <Input
                        id={`overlay-${idx}-end`}
                        type="number"
                        step="0.1"
                        min={0.1}
                        max={Math.max(totalDuration + 0.5, 1)}
                        value={b.end_s}
                        onChange={(e) =>
                          handleUpdate(
                            idx,
                            'end_s',
                            Math.max(0.1, Number(e.target.value) || 0.1),
                          )
                        }
                        disabled={!canEdit}
                      />
                    </div>
                  </div>

                  {/* Position grid */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Position</Label>
                    <div
                      className="grid grid-cols-3 gap-1 w-fit"
                      role="grid"
                      aria-label="Position picker"
                    >
                      {POSITION_PRESETS.map((p) => {
                        const active = presetKey === p.key
                        return (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => handleSetPosition(idx, p.x, p.y)}
                            disabled={!canEdit}
                            aria-label={p.label}
                            aria-pressed={active}
                            className={cn(
                              'w-7 h-7 rounded text-[10px] font-semibold transition-colors',
                              active
                                ? 'bg-foreground text-background'
                                : 'bg-muted text-muted-foreground hover:bg-accent',
                            )}
                          >
                            {p.key}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Style row: font size + color + shadow */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Font size</Label>
                      <Select
                        value={String(b.font_size)}
                        onValueChange={(v) => handleUpdate(idx, 'font_size', Number(v))}
                        disabled={!canEdit}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONT_SIZE_PRESETS.map((s) => (
                            <SelectItem key={s} value={String(s)}>
                              {s}px
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Color</Label>
                      <Select
                        value={b.color}
                        onValueChange={(v) => handleUpdate(idx, 'color', v)}
                        disabled={!canEdit}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COLOR_OPTIONS.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className="inline-block w-3 h-3 rounded-sm border border-border/50"
                                  style={{ background: c.value }}
                                />
                                {c.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        checked={b.shadow}
                        onChange={(e) => handleUpdate(idx, 'shadow', e.target.checked)}
                        disabled={!canEdit}
                        className="h-4 w-4 cursor-pointer accent-foreground"
                      />
                      Drop shadow
                    </label>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={blocks.length >= MAX_BLOCKS || !canEdit}
          className="w-full"
        >
          <Plus className="h-4 w-4" />
          Add text block
        </Button>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {(blocks.length > 0 || dirty) && (
          <>
            <Separator />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {dirty && (
                <span className="text-[11px] text-muted-foreground mr-auto">
                  Unsaved changes
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={!dirty || saving || !canEdit}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleApply}
                // Explicit isApplying check matches the server-side
                // idempotency guard — even if the local `applying` flag
                // dropped (network race), polling-driven status === 'applying_overlays'
                // keeps this disabled until the worker reports back.
                disabled={
                  applying ||
                  isApplying ||
                  status === 'applying_overlays' ||
                  (blocks.length === 0 && serverSnapshot.length === 0)
                }
              >
                {(applying || isApplying) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Apply to video
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
